import { useQuery, useMutation } from "@tanstack/react-query";
import { QueryModule } from "@/data/queryModules";
import { MutationEffectsOptions, useMutationEffects } from "@/data/useMutationEffects";
import { AppQueryOptions, AppMutationOptions } from "@povio/openapi-codegen-cli";
import { ProfileModels } from "./profile.models";
import { ProfileApi } from "./profile.api";

export namespace ProfileQueries {

export const moduleName = QueryModule.Profile;

export const keys = {
    all: [moduleName] as const,
    getMy: () => [...keys.all, "/profile/me", ] as const,
    reset: () => [...keys.all, "/profile/me/reset", ] as const,
    getJobSkillMatrix: () => [...keys.all, "/profile/me/job-skill-matrix", ] as const,
    getBlsSkillIndustry: () => [...keys.all, "/profile/me/bls-skill-industry", ] as const,
};


 /** 
 * Query `useGetMy`
 * @summary Return user and their profile data
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<ProfileModels.FullProfileResponseDto> } 
 * @statusCodes [200, 401]
 */
export const useGetMy = <TData>(options?: AppQueryOptions<typeof ProfileApi.getMy, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getMy(),
    queryFn: ProfileApi.getMy
    ,
    ...options,
  });
};








 /** 
 * Mutation `useUpdate`
 * @param { ProfileModels.UpdateProfileDto } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<ProfileModels.FullProfileResponseDto> } 
 * @statusCodes [200, 401]
 */
export const useUpdate = (options?: AppMutationOptions<typeof ProfileApi.update, { data: ProfileModels.UpdateProfileDto,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { data } ) => 
      
            ProfileApi.update(data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      
      const updateKeys = [keys.getMy(), ];
      await runMutationEffects(resData, options, updateKeys);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useUpdateIntroduction`
 * @param { ProfileModels.UpdateIntroductionDto } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<void> } 
 * @statusCodes [204, 401]
 */
export const useUpdateIntroduction = (options?: AppMutationOptions<typeof ProfileApi.updateIntroduction, { data: ProfileModels.UpdateIntroductionDto,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { data } ) => 
      
            ProfileApi.updateIntroduction(data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      
      const updateKeys = [keys.reset(), ];
      await runMutationEffects(resData, options, updateKeys);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useUpdateVisibility`
 * @param { ProfileModels.ProfileFieldChangeVisibilityRequest } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<ProfileModels.FullProfileResponseDto> } 
 * @statusCodes [200, 401]
 */
export const useUpdateVisibility = (options?: AppMutationOptions<typeof ProfileApi.updateVisibility, { data: ProfileModels.ProfileFieldChangeVisibilityRequest,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { data } ) => 
      
            ProfileApi.updateVisibility(data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      
      const updateKeys = [keys.getMy(), ];
      await runMutationEffects(resData, options, updateKeys);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};





 /** 
 * Query `useReset`
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<void> } 
 * @statusCodes [204, 401]
 */
export const useReset = <TData>(options?: AppQueryOptions<typeof ProfileApi.reset, TData>) => {
  
  
  return useQuery({
    queryKey: keys.reset(),
    queryFn: ProfileApi.reset
    ,
    ...options,
  });
};








 /** 
 * Mutation `useRequestPictureUpload`
 * @param { ProfileModels.MediaUploadRequest } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<CommonModels.MediaUploadInstructions> } 
 * @statusCodes [200, 201, 401]
 */
export const useRequestPictureUpload = (options?: AppMutationOptions<typeof ProfileApi.requestPictureUpload, { data: ProfileModels.MediaUploadRequest,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { data } ) => 
      
            ProfileApi.requestPictureUpload(data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      await runMutationEffects(resData, options);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useValidatePictureUpload`
 * @param { ProfileModels.MediaAttachRequestDto } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<ProfileModels.MediaValidatedResponse> } 
 * @statusCodes [200, 401]
 */
export const useValidatePictureUpload = (options?: AppMutationOptions<typeof ProfileApi.validatePictureUpload, { data: ProfileModels.MediaAttachRequestDto,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { data } ) => 
      
            ProfileApi.validatePictureUpload(data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      await runMutationEffects(resData, options);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useRequestIntroductionCoverImageUpload`
 * @param { ProfileModels.MediaUploadRequest } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<CommonModels.MediaUploadInstructions> } 
 * @statusCodes [200, 201, 401]
 */
export const useRequestIntroductionCoverImageUpload = (options?: AppMutationOptions<typeof ProfileApi.requestIntroductionCoverImageUpload, { data: ProfileModels.MediaUploadRequest,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { data } ) => 
      
            ProfileApi.requestIntroductionCoverImageUpload(data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      await runMutationEffects(resData, options);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useValidateIntroductionCoverImageUpload`
 * @param { ProfileModels.MediaAttachRequestDto } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<ProfileModels.MediaValidatedResponse> } 
 * @statusCodes [200, 401]
 */
export const useValidateIntroductionCoverImageUpload = (options?: AppMutationOptions<typeof ProfileApi.validateIntroductionCoverImageUpload, { data: ProfileModels.MediaAttachRequestDto,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { data } ) => 
      
            ProfileApi.validateIntroductionCoverImageUpload(data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      await runMutationEffects(resData, options);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useDeleteIntroductionCoverImage`
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<void> } 
 * @statusCodes [204, 401]
 */
export const useDeleteIntroductionCoverImage = (options?: AppMutationOptions<typeof ProfileApi.deleteIntroductionCoverImage, {  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: () => 
      
            ProfileApi.deleteIntroductionCoverImage()
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      
      const updateKeys = [keys.reset(), ];
      await runMutationEffects(resData, options, updateKeys);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};





 /** 
 * Query `useGetJobSkillMatrix`
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<CommonModels.ProfileJobSkillMatrixResponseDto> } 
 * @statusCodes [200, 401]
 */
export const useGetJobSkillMatrix = <TData>(options?: AppQueryOptions<typeof ProfileApi.getJobSkillMatrix, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getJobSkillMatrix(),
    queryFn: ProfileApi.getJobSkillMatrix
    ,
    ...options,
  });
};






 /** 
 * Query `useGetBlsSkillIndustry`
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<CommonModels.ProfileBlsSkillIndustryResponseDto> } 
 * @statusCodes [200, 401]
 */
export const useGetBlsSkillIndustry = <TData>(options?: AppQueryOptions<typeof ProfileApi.getBlsSkillIndustry, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getBlsSkillIndustry(),
    queryFn: ProfileApi.getBlsSkillIndustry
    ,
    ...options,
  });
};








 /** 
 * Mutation `useGenerateBio`
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<ProfileModels.GenerateBioResponseDto> } Generated bio text
 * @statusCodes [200, 401]
 */
export const useGenerateBio = (options?: AppMutationOptions<typeof ProfileApi.generateBio, {  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: () => 
      
            ProfileApi.generateBio()
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      await runMutationEffects(resData, options);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useUpdateSkills`
 * @param { ProfileModels.UpdateSkillsDto } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<ProfileModels.ProfileSkillsResponseDto> } 
 * @statusCodes [200, 401]
 */
export const useUpdateSkills = (options?: AppMutationOptions<typeof ProfileApi.updateSkills, { data: ProfileModels.UpdateSkillsDto,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { data } ) => 
      
            ProfileApi.updateSkills(data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      await runMutationEffects(resData, options);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useUpdatePositionInfo`
 * @param { ProfileModels.UpdatePositionInfoDto } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<void> } 
 * @statusCodes [204, 401]
 */
export const useUpdatePositionInfo = (options?: AppMutationOptions<typeof ProfileApi.updatePositionInfo, { data: ProfileModels.UpdatePositionInfoDto,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { data } ) => 
      
            ProfileApi.updatePositionInfo(data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      
      const updateKeys = [keys.reset(), ];
      await runMutationEffects(resData, options, updateKeys);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useUpdatePersonalInfo`
 * @param { ProfileModels.UpdatePersonalInfoDto } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<void> } 
 * @statusCodes [204, 401]
 */
export const useUpdatePersonalInfo = (options?: AppMutationOptions<typeof ProfileApi.updatePersonalInfo, { data: ProfileModels.UpdatePersonalInfoDto,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { data } ) => 
      
            ProfileApi.updatePersonalInfo(data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      
      const updateKeys = [keys.reset(), ];
      await runMutationEffects(resData, options, updateKeys);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useUpdateWorkExperience`
 * @param { ProfileModels.UpdateWorkExperienceDto } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<void> } 
 * @statusCodes [204, 401]
 */
export const useUpdateWorkExperience = (options?: AppMutationOptions<typeof ProfileApi.updateWorkExperience, { data: ProfileModels.UpdateWorkExperienceDto,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { data } ) => 
      
            ProfileApi.updateWorkExperience(data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      
      const updateKeys = [keys.reset(), ];
      await runMutationEffects(resData, options, updateKeys);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useUpdateEducations`
 * @param { ProfileModels.UpdateEducationsDto } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<void> } 
 * @statusCodes [204, 401]
 */
export const useUpdateEducations = (options?: AppMutationOptions<typeof ProfileApi.updateEducations, { data: ProfileModels.UpdateEducationsDto,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { data } ) => 
      
            ProfileApi.updateEducations(data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      
      const updateKeys = [keys.reset(), ];
      await runMutationEffects(resData, options, updateKeys);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useUpdateVolunteerInfo`
 * @param { ProfileModels.UpdateVolunteersDto } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<void> } 
 * @statusCodes [204, 401]
 */
export const useUpdateVolunteerInfo = (options?: AppMutationOptions<typeof ProfileApi.updateVolunteerInfo, { data: ProfileModels.UpdateVolunteersDto,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { data } ) => 
      
            ProfileApi.updateVolunteerInfo(data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      
      const updateKeys = [keys.reset(), ];
      await runMutationEffects(resData, options, updateKeys);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useUpdateCertificationsInfo`
 * @param { ProfileModels.UpdateCertificationsDto } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<void> } 
 * @statusCodes [204, 401]
 */
export const useUpdateCertificationsInfo = (options?: AppMutationOptions<typeof ProfileApi.updateCertificationsInfo, { data: ProfileModels.UpdateCertificationsDto,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { data } ) => 
      
            ProfileApi.updateCertificationsInfo(data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      
      const updateKeys = [keys.reset(), ];
      await runMutationEffects(resData, options, updateKeys);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useUpdateAchievementsInfo`
 * @param { ProfileModels.UpdateAchievementsDto } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<void> } 
 * @statusCodes [204, 401]
 */
export const useUpdateAchievementsInfo = (options?: AppMutationOptions<typeof ProfileApi.updateAchievementsInfo, { data: ProfileModels.UpdateAchievementsDto,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { data } ) => 
      
            ProfileApi.updateAchievementsInfo(data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      
      const updateKeys = [keys.reset(), ];
      await runMutationEffects(resData, options, updateKeys);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};





}
